import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faXmark,
  faArrowRight,
  faInfinity,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { useSignupEnabled } from '../hooks/useSignupEnabled';
import { usePageMeta } from '../hooks/usePageMeta';

/* ─────────────────────────── TIER DATA ─────────────────────────────────── */

interface TierFeature {
  label: string;
  included: boolean;
}

interface Tier {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  lifetimePrice?: number;
  description: string;
  credits: number;
  concurrency: number;
  queuePriority: string;
  cta: string;
  featured: boolean;
  features: TierFeature[];
}

const CREDIT_RATES = { backtest: '1 cr / candle', optimization: 'compute-based dynamic', wfa: 'opt cost x periods' };

const formatPlanCredits = (credits: number): string => {
  switch (credits) {
    case 50000:
      return '50K';
    case 300000:
      return '300K';
    case 1200000:
      return '1.2 Million';
    case 3000000:
      return '3 Million';
    default:
      return credits.toLocaleString();
  }
};

const tiers: Tier[] = [
  {
    name: 'Starter',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Explore the platform with basic backtesting. No credit card required.',
    credits: 50000,
    concurrency: 1,
    queuePriority: 'Lowest',
    cta: 'Start Exploring',
    featured: false,
    features: [
      { label: 'Basic backtesting', included: true },
      { label: '50K credits / month', included: true },
      { label: 'Built-in strategies', included: true },
      { label: 'Optimization', included: false },
      { label: 'Walk-forward analysis', included: false },
      { label: 'Credit rollover', included: false },
    ],
  },
  {
    name: 'Independent',
    monthlyPrice: 39,
    yearlyPrice: 389,
    description: 'Best for exploring ideas and learning the platform.',
    credits: 300000,
    concurrency: 1,
    queuePriority: 'Standard',
    cta: 'Start Research',
    featured: false,
    features: [
      { label: 'Full platform access', included: true },
      { label: '1 concurrent research job', included: true },
      { label: 'Standard compute queue', included: true },
      { label: 'Community support', included: true },
      { label: 'Credit rollover (1x monthly limit)', included: true },
      { label: 'Priority support', included: false },
      { label: 'Early access to new tools', included: false },
    ],
  },
  {
    name: 'Professional',
    monthlyPrice: 99,
    yearlyPrice: 989,
    lifetimePrice: 999,
    description: 'For active strategy research and serious experimentation.',
    credits: 1200000,
    concurrency: 2,
    queuePriority: 'Elevated',
    cta: 'Start Research',
    featured: true,
    features: [
      { label: 'Full platform access', included: true },
      { label: '2 concurrent research jobs', included: true },
      { label: 'Elevated compute priority', included: true },
      { label: 'Credit rollover (2x monthly limit)', included: true },
      { label: 'Priority support', included: true },
      { label: 'Early access to new tools', included: false },
    ],
  },
  {
    name: 'Advanced',
    monthlyPrice: 159,
    yearlyPrice: 1589,
    description: 'For heavy research workflows and large optimization runs.',
    credits: 3000000,
    concurrency: 4,
    queuePriority: 'Highest',
    cta: 'Start Research',
    featured: false,
    features: [
      { label: 'Full platform access', included: true },
      { label: '4 concurrent research jobs', included: true },
      { label: 'Highest compute priority', included: true },
      { label: 'Credit rollover (3x monthly limit)', included: true },
      { label: 'Priority support', included: true },
      { label: 'Early access to new research tools', included: true },
    ],
  },
];

const faqs = [
  {
    q: 'Can I change plans at any time?',
    a: 'Yes. Upgrades and downgrades take effect at the end of the current billing cycle. Your data is preserved.',
  },
  {
    q: 'What about early access pricing?',
    a: 'Early access seats include a discounted rate locked in for the lifetime of the subscription. This pricing will not be available after general launch.',
  },
  {
    q: 'What happens to my data if I downgrade?',
    a: 'All strategies and backtests are preserved. You retain read access to everything, but credit allocation adjusts to the lower tier.',
  },
  {
    q: 'What are research credits?',
    a: 'Credits are consumed based on compute workload: backtests are 1 credit per candle, optimization and WFA are dynamically priced from candles and iteration depth. Credits reset monthly and unused credits roll over based on your plan.',
  },
  {
    q: 'Do you offer refunds?',
    a: '14-day money-back guarantee on all plans. No questions asked.',
  },
];

/* ─────────────────────────────── PAGE ──────────────────────────────────── */

const PricingPage: React.FC = () => {
  usePageMeta({ title: 'Pricing', description: 'Transparent pricing for Quanthop. Backtesting, optimization, and walk-forward validation tools at every level. Early access seats include lifetime discounted rates.' });
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly' | 'lifetime'>('monthly');
  const { signupEnabled } = useSignupEnabled();

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 font-[Poppins] selection:bg-[#aa48ff]/30">
      <PublicNavBar />

      {/* Header */}
      <section className="pt-32 pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-mono tracking-widest uppercase text-[#aa48ff] mb-4">
            Pricing
          </p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Structured research tools at every level.
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto mb-10">
            Transparent pricing. Early access seats include lifetime discounted rates.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center">
            <div className="inline-flex rounded-lg bg-gray-800 p-1">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${billingInterval === 'monthly'
                  ? 'bg-gray-700 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-300'
                  }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${billingInterval === 'yearly'
                  ? 'bg-gray-700 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-300'
                  }`}
              >
                Yearly
                <span className="ml-1.5 text-xs text-emerald-400 font-mono">-17%</span>
              </button>
              <button
                onClick={() => setBillingInterval('lifetime')}
                className={`px-5 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${billingInterval === 'lifetime'
                  ? 'bg-gray-700 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-300'
                  }`}
              >
                <FontAwesomeIcon icon={faInfinity} className="text-xs" />
                Lifetime
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => {
            const isFree = tier.monthlyPrice === 0;
            const isLifetime = billingInterval === 'lifetime';
            const isYearly = billingInterval === 'yearly';
            const hasLifetime = !!tier.lifetimePrice;
            const isUnavailable = isLifetime && !hasLifetime;
            const price = isFree ? 0
              : isLifetime && hasLifetime ? tier.lifetimePrice!
                : isYearly ? tier.yearlyPrice : tier.monthlyPrice;
            const period = isFree ? 'forever'
              : isLifetime && hasLifetime ? 'one-time'
                : isYearly ? '/year' : '/month';
            const billingParam = isLifetime && hasLifetime
              ? 'lifetime'
              : isYearly ? 'yearly' : 'monthly';

            return (
              <div
                key={tier.name}
                className={`rounded-xl p-7 flex flex-col ${isUnavailable ? 'opacity-50' : ''} ${tier.featured
                  ? 'bg-gray-900/80 border-2 border-[#aa48ff]/60 ring-1 ring-[#aa48ff]/20'
                  : 'bg-gray-900/50 border border-gray-800'
                  }`}
              >
                <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                <p className="text-xs text-gray-500 mb-5">{tier.description}</p>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold">
                    {isFree ? 'Free' : `$${price}`}
                  </span>
                  {isFree && <span className="text-sm text-gray-500 ml-1">forever</span>}
                  {!isFree && <span className="text-sm text-gray-500">{period}</span>}
                </div>

                {isYearly && !isFree && (
                  <p className="text-xs text-emerald-400 font-mono mb-5">
                    ${(tier.yearlyPrice / 12).toFixed(2)}/month
                  </p>
                )}
                {isLifetime && hasLifetime && !isFree && (
                  <p className="text-xs text-[#aa48ff] font-mono mb-5">
                    Pay once, access forever
                  </p>
                )}
                {isLifetime && !hasLifetime && !isFree && (
                  <p className="text-xs text-gray-600 mb-5">
                    Lifetime not available for this plan
                  </p>
                )}
                {((!isYearly && !isLifetime) || isFree) && <div className="mb-5" />}

                {/* Credits headline */}
                <div className="bg-gray-800/50 rounded-lg p-4 mb-5 border border-gray-700/50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#aa48ff]">
                      {formatPlanCredits(tier.credits)} Credits / Month
                    </p>
                    <div className="relative group">
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="text-gray-500 hover:text-gray-300 cursor-help text-sm transition-colors"
                      />
                      <div className="absolute right-0 bottom-full mb-2 w-52 rounded-lg bg-gray-800 border border-gray-700 p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-10">
                        <p className="text-xs font-semibold text-gray-200 mb-2">Credit Usage</p>
                        <div className="space-y-1.5 text-[11px]">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Backtests</span>
                            <span className="text-gray-300 font-mono">{CREDIT_RATES.backtest}</span>
                          </div>
                          {!isFree && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Optimizations</span>
                                <span className="text-gray-300 font-mono">{CREDIT_RATES.optimization}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">WFA</span>
                                <span className="text-gray-300 font-mono">{CREDIT_RATES.wfa}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {signupEnabled === false || isUnavailable ? (
                  <span
                    className="block w-full py-3 rounded-lg font-semibold text-sm text-center bg-gray-800 text-gray-500 cursor-not-allowed mb-7"
                  >
                    {isUnavailable ? 'Not Available' : !signupEnabled ? 'Coming Soon' : 'Coming Soon'}
                  </span>
                ) : (
                  <Link
                    to={isFree ? '/signup' : `/signup?plan=${tier.name.toLowerCase()}&billing=${billingParam}`}
                    className={`block w-full py-3 rounded-lg font-semibold text-sm text-center transition-colors mb-7 ${tier.featured
                      ? 'bg-[#aa48ff] hover:bg-[#8c3acc] text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                      }`}
                  >
                    {isLifetime && hasLifetime ? 'Get Lifetime Access' : tier.cta}
                  </Link>
                )}

                <ul className="space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f.label} className="flex items-start gap-2.5">
                      <FontAwesomeIcon
                        icon={f.included ? faCheck : faXmark}
                        className={`mt-0.5 text-xs flex-shrink-0 ${f.included ? 'text-emerald-400' : 'text-gray-700'
                          }`}
                      />
                      <span
                        className={`text-[13px] leading-relaxed ${f.included ? 'text-gray-300' : 'text-gray-600'
                          }`}
                      >
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-800/60">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            Frequently asked questions
          </h2>
          <div className="space-y-5">
            {faqs.map((faq) => (
              <div
                key={faq.q}
                className="p-5 rounded-xl bg-gray-900/50 border border-gray-800"
              >
                <h3 className="font-semibold text-sm text-gray-100 mb-2">
                  {faq.q}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-800/60">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to start researching?
          </h2>
          <p className="text-gray-400 text-sm mb-8">
            Start your research with early access pricing.
          </p>
          {signupEnabled !== true ? (
            <span
              className="inline-flex items-center gap-3 px-8 py-4 bg-gray-800 text-gray-500 rounded-lg font-semibold text-base cursor-not-allowed"
            >
              Coming Soon
            </span>
          ) : (
            <Link
              to="/signup?plan=professional"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#aa48ff] hover:bg-[#8c3acc] text-white rounded-lg font-semibold text-base transition-colors"
            >
              Start Research
              <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
            </Link>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PricingPage;
